import { v4 as uuidV4 } from 'uuid';
import i18n from 'src/i18n';
import {
  Audience,
  ReportConfig,
  ReportLanguage,
  ReportSectionOptions,
  ReportType,
  SectionDefinition,
  SectionId,
  TemplateId,
} from './types';

export const DEFAULT_REPORT_ACCENT_COLOR = '#165D5C';

const translateReportBuilder = (
  key: string,
  options?: Record<string, unknown>
) => i18n.t(`lca:reportBuilder.${key}`, options) as string;

export const REPORT_BUILDER_STEPS = [
  'Brand & Cover',
  'Content',
  'Custom Content',
  'Layout',
  'Preview',
  'Generate',
] as const;

export const ONE_PAGER_REPORT_BUILDER_STEPS = [
  'One-Pager Setup',
  'Preview',
  'Generate',
] as const;

export const REPORT_TYPE_OPTIONS: Array<{
  id: ReportType;
  label: string;
  description: string;
}> = [
  {
    id: 'environmental-declaration-report',
    label: 'Environmental Declaration Report',
    description:
      'Full LCA report for declaration-style output. Uses the current standard report layout.',
  },
  {
    id: 'product-environmental-footprint-report',
    label: 'Product Environmental Footprint Report',
    description:
      'Full LCA report aligned to Product Environmental Footprint style output. Uses the current standard report layout for now.',
  },
  {
    id: 'lca-one-pager',
    label: 'LCA One-Pager',
    description:
      'Condensed fixed-layout LCA report generated as a single A4 PDF page.',
  },
];

export const getDefaultReportTitle = (
  reportType: ReportType,
  language?: ReportLanguage
): string => getReportTypeLabel(reportType, language);

export const getDefaultReportName = (
  productName: string,
  date: string,
  language?: ReportLanguage
): string =>
  translateReportBuilder('defaults.reportName', {
    lng: language,
    productName,
    date,
  });

const getDefaultSectionOptions = (
  sectionId: SectionId
): ReportSectionOptions => {
  if (sectionId === 'results-ghg') {
    return {
      includeCharts: true,
      includeHotspots: true,
    };
  }

  if (sectionId === 'impact-categories') {
    return {
      includeCharts: true,
      includeAdditionalEnvironmentalData: true,
    };
  }

  if (sectionId === 'system-boundaries') {
    return {
      includeFlowchart: true,
      includeImpactValues: true,
    };
  }

  return {};
};

export const REPORT_SECTIONS: SectionDefinition[] = [
  {
    id: 'executive-summary',
    title: 'Executive Summary',
    description:
      'High-level footprint overview with stage split and hotspot insights.',
    supportsDetailLevel: true,
    defaultEnabled: true,
  },
  {
    id: 'methodology-standards',
    title: 'Methodology & Standards',
    description:
      'Standards references, assumptions, allocation, and quality statement.',
    supportsDetailLevel: true,
    defaultEnabled: true,
  },
  {
    id: 'producer-info',
    title: 'Producer / Organization Info',
    description: 'Company profile and producer details.',
    supportsDetailLevel: true,
    defaultEnabled: true,
  },
  {
    id: 'product-description',
    title: 'Product Description',
    description:
      'Functional unit, dimensions, materials, recycled content, and product metadata.',
    supportsDetailLevel: true,
    defaultEnabled: true,
  },
  {
    id: 'materials-circularity',
    title: 'Materials & Circularity',
    description:
      'Material mix, recycled share, circularity notes, and end-of-life inputs from the platform.',
    supportsDetailLevel: true,
    defaultEnabled: true,
  },
  {
    id: 'durability-end-of-life',
    title: 'Durability & End-of-Life',
    description:
      'Service life, repairability, spare parts, disassembly, and recycling instructions.',
    supportsDetailLevel: true,
    defaultEnabled: true,
  },
  {
    id: 'data-quality-readiness',
    title: 'Data Quality & Readiness',
    description:
      'Shows how complete the product, methodology, circularity, and durability data is inside the platform.',
    supportsDetailLevel: true,
    defaultEnabled: true,
  },
  {
    id: 'system-boundaries',
    title: 'System Boundaries & Life Cycle Stages',
    description: 'A1-A5, B, C model scope and stage diagram context.',
    supportsDetailLevel: true,
    defaultEnabled: true,
  },
  {
    id: 'results-ghg',
    title: 'Results (GHG)',
    description: 'CO2e table and charted stage-level emissions.',
    supportsDetailLevel: true,
    defaultEnabled: true,
  },
  {
    id: 'impact-categories',
    title: 'Impact Categories',
    description: 'EN 15804 / PEF-aligned impact categories by stage and total.',
    supportsDetailLevel: true,
    defaultEnabled: true,
  },
  {
    id: 'interpretation',
    title: 'Interpretation',
    description:
      'Auto-generated narrative interpretation from computed results.',
    supportsDetailLevel: true,
    defaultEnabled: true,
  },
  {
    id: 'scenarios',
    title: 'Scenarios',
    description: 'Before/after and what-if comparisons where data exists.',
    supportsDetailLevel: true,
    defaultEnabled: false,
  },
  {
    id: 'bibliography',
    title: 'Bibliography / Sources',
    description: 'Data and methodological source references.',
    supportsDetailLevel: false,
    defaultEnabled: true,
  },
  {
    id: 'disclaimer-audit-trail',
    title: 'Disclaimer & Audit Trail',
    description: 'Report ID, hash, model version, and dataset versions.',
    supportsDetailLevel: false,
    defaultEnabled: false,
  },
];

const REPORT_BUILDER_STEP_KEY_MAP: Record<string, string> = {
  'Brand & Cover': 'steps.brandCover',
  Content: 'steps.content',
  'Custom Content': 'steps.customContent',
  Layout: 'steps.layout',
  Preview: 'steps.preview',
  Generate: 'steps.generate',
  'One-Pager Setup': 'steps.onePagerSetup',
};

const REPORT_TYPE_KEY_MAP: Record<
  ReportType,
  { label: string; description: string }
> = {
  'environmental-declaration-report': {
    label: 'reportTypes.environmentalDeclaration.label',
    description: 'reportTypes.environmentalDeclaration.description',
  },
  'product-environmental-footprint-report': {
    label: 'reportTypes.productEnvironmentalFootprint.label',
    description: 'reportTypes.productEnvironmentalFootprint.description',
  },
  'lca-one-pager': {
    label: 'reportTypes.lcaOnePager.label',
    description: 'reportTypes.lcaOnePager.description',
  },
};

const REPORT_SECTION_KEY_MAP: Record<
  SectionId,
  { title: string; description: string }
> = {
  'executive-summary': {
    title: 'sections.executiveSummary.title',
    description: 'sections.executiveSummary.description',
  },
  'methodology-standards': {
    title: 'sections.methodologyStandards.title',
    description: 'sections.methodologyStandards.description',
  },
  'producer-info': {
    title: 'sections.producerInfo.title',
    description: 'sections.producerInfo.description',
  },
  'product-description': {
    title: 'sections.productDescription.title',
    description: 'sections.productDescription.description',
  },
  'materials-circularity': {
    title: 'sections.materialsCircularity.title',
    description: 'sections.materialsCircularity.description',
  },
  'durability-end-of-life': {
    title: 'sections.durabilityEndOfLife.title',
    description: 'sections.durabilityEndOfLife.description',
  },
  'data-quality-readiness': {
    title: 'sections.dataQualityReadiness.title',
    description: 'sections.dataQualityReadiness.description',
  },
  'system-boundaries': {
    title: 'sections.systemBoundaries.title',
    description: 'sections.systemBoundaries.description',
  },
  'results-ghg': {
    title: 'sections.resultsGhg.title',
    description: 'sections.resultsGhg.description',
  },
  'impact-categories': {
    title: 'sections.impactCategories.title',
    description: 'sections.impactCategories.description',
  },
  interpretation: {
    title: 'sections.interpretation.title',
    description: 'sections.interpretation.description',
  },
  scenarios: {
    title: 'sections.scenarios.title',
    description: 'sections.scenarios.description',
  },
  bibliography: {
    title: 'sections.bibliography.title',
    description: 'sections.bibliography.description',
  },
  'disclaimer-audit-trail': {
    title: 'sections.disclaimerAuditTrail.title',
    description: 'sections.disclaimerAuditTrail.description',
  },
};

export const getReportBuilderStepLabel = (step: string, language?: string) =>
  translateReportBuilder(
    REPORT_BUILDER_STEP_KEY_MAP[step] || 'steps.generate',
    {
      lng: language,
      defaultValue: step,
    }
  );

export const getReportTypeLabel = (reportType: ReportType, language?: string) =>
  translateReportBuilder(REPORT_TYPE_KEY_MAP[reportType].label, {
    lng: language,
  });

export const getReportTypeDescription = (
  reportType: ReportType,
  language?: string
) =>
  translateReportBuilder(REPORT_TYPE_KEY_MAP[reportType].description, {
    lng: language,
  });

export const getReportSectionTitle = (
  sectionId: SectionId,
  language?: string
) =>
  translateReportBuilder(REPORT_SECTION_KEY_MAP[sectionId].title, {
    lng: language,
  });

export const getReportSectionDescription = (
  sectionId: SectionId,
  language?: string
) =>
  translateReportBuilder(REPORT_SECTION_KEY_MAP[sectionId].description, {
    lng: language,
  });

export const SECTION_ID_TO_STAGE_LABEL: Record<string, string> = {
  'raw-materials-extraction-and-processing': 'A1',
  'transport-of-raw-materials': 'A2',
  'production-and-packaging': 'A3',
  'distribution-and-storage': 'A4',
  installation: 'A5',
  'use-stage': 'B',
  'end-of-life': 'C',
};

export const AUDIENCE_DEFAULTS: Record<
  Audience,
  {
    templateId: TemplateId;
    requiredSections: SectionId[];
    defaultDetailedSections: SectionId[];
    watermarkDefault: boolean;
    density: 'spacious' | 'standard' | 'compact';
  }
> = {
  'client-ready': {
    templateId: 'modern',
    requiredSections: ['executive-summary', 'results-ghg'],
    defaultDetailedSections: [],
    watermarkDefault: false,
    density: 'standard',
  },
  'audit-ready': {
    templateId: 'audit-ready',
    requiredSections: ['methodology-standards', 'disclaimer-audit-trail'],
    defaultDetailedSections: [
      'methodology-standards',
      'results-ghg',
      'impact-categories',
    ],
    watermarkDefault: false,
    density: 'compact',
  },
  internal: {
    templateId: 'compact',
    requiredSections: ['executive-summary'],
    defaultDetailedSections: ['results-ghg'],
    watermarkDefault: true,
    density: 'standard',
  },
};

export const EN_PL_LABELS = {
  en: {
    previewTitle: 'Report Preview',
    noData: 'Data not available for selected scope.',
    notProvided: 'Not provided',
    generatedNarrativePrefix: 'Interpretation generated from stage profile:',
  },
  pl: {
    previewTitle: 'Podgląd Raportu',
    noData: 'Brak danych dla wybranego zakresu.',
    notProvided: 'Nie podano',
    generatedNarrativePrefix:
      'Interpretacja wygenerowana na podstawie profilu etapów:',
  },
  sv: {
    previewTitle: 'Rapportförhandsvisning',
    noData: 'Data är inte tillgänglig för det valda omfånget.',
    notProvided: 'Inte angivet',
    generatedNarrativePrefix: 'Tolkning genererad från etapprofilen:',
  },
} satisfies Record<
  ReportLanguage,
  {
    previewTitle: string;
    noData: string;
    notProvided: string;
    generatedNarrativePrefix: string;
  }
>;

export const REPORT_CONFIG_JSON_SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: 'https://envirly.com/schemas/report-config.json',
  title: 'ReportConfig',
  type: 'object',
  additionalProperties: false,
  required: [
    'reportId',
    'seriesId',
    'projectId',
    'productId',
    'language',
    'reportType',
    'templateId',
    'branding',
    'layout',
    'sections',
    'output',
    'audit',
  ],
  properties: {
    reportId: { type: 'string', format: 'uuid' },
    seriesId: { type: 'string', format: 'uuid' },
    projectId: { type: 'string' },
    productId: { type: 'string' },
    language: { type: 'string', enum: ['en', 'pl', 'sv'] },
    languageSource: { type: 'string', enum: ['auto', 'manual'] },
    reportType: {
      type: 'string',
      enum: REPORT_TYPE_OPTIONS.map((option) => option.id),
    },
    templateId: { type: 'string', enum: ['modern', 'compact', 'audit-ready'] },
    audience: {
      type: 'string',
      enum: ['client-ready', 'audit-ready', 'internal'],
    },
    sections: {
      type: 'array',
      minItems: 1,
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['id', 'enabled', 'detailLevel', 'options'],
        properties: {
          id: {
            type: 'string',
            enum: REPORT_SECTIONS.map((section) => section.id),
          },
          enabled: { type: 'boolean' },
          detailLevel: {
            type: 'string',
            enum: ['summary', 'standard', 'detailed'],
          },
          options: {
            type: 'object',
            additionalProperties: true,
          },
        },
      },
    },
  },
} as const;

export const REPORT_CONFIG_EXAMPLE = {
  reportId: '151083bc-c7fd-4dd1-aa49-013834f9ac04',
  seriesId: '2f235af7-6b60-4da7-95ea-2a11031f34cb',
  projectId: 'org_4821',
  productId: 'prod_floorpanel_001',
  language: 'en',
  languageSource: 'auto',
  reportType: 'environmental-declaration-report',
  templateId: 'modern',
  audience: 'client-ready',
  branding: {
    logoAssetId: 'asset_logo_991',
    logoUrl: 'https://example.com/logo.png',
    companyNameOverride: 'Nordic Build Sp. z o.o.',
    reportTitle: 'Environmental Declaration Report',
    productName: 'EcoFloor Panel 12mm',
    showDate: true,
    accentIntensity: 'medium',
    accentColor: DEFAULT_REPORT_ACCENT_COLOR,
    coverStyle: 'envirly',
    coverAssetId: null,
    coverUrl: null,
  },
  layout: {
    density: 'standard',
    chartStyle: 'donut-bars',
    numberFormat: {
      decimals: 2,
      thousandsSeparator: ' ',
      showUnits: true,
    },
    watermark: {
      enabled: false,
      label: 'CONFIDENTIAL',
    },
    footerOptions: {
      showContactInfo: true,
      showWebsite: true,
      showPageNumbers: true,
    },
  },
  sections: REPORT_SECTIONS.map((section) => ({
    id: section.id,
    enabled: section.defaultEnabled,
    detailLevel: 'standard',
    options: getDefaultSectionOptions(section.id),
  })),
  output: {
    formats: ['pdf'],
    reportName: 'LCA Report - EcoFloor Panel 12mm - 2026-03-03',
  },
  audit: {
    datasetVersions: {
      ecoinvent: '3.10',
      defra: '2025.1',
      aib: '2025.2',
      factorsVersion: 'lca-factors-2026.02',
    },
    generatedByUserId: 'usr_724',
    generatedAt: '2026-03-03T09:28:12Z',
    hash: '6e18c86d46d6f1c7a8d6ef9b31f0f0f4181d3e31db4bfc8d3a4f41243d0b8e62',
    modelVersion: 'report-builder-1.0.0',
  },
} as const;

type DefaultConfigInput = {
  productId: number;
  organizationId: number;
  productName: string;
  organizationName: string;
  organizationLogo?: { id: string; url: string } | null;
  userId: string;
  language?: ReportLanguage;
};

export const createDefaultReportConfig = ({
  productId,
  organizationId,
  productName,
  organizationName,
  organizationLogo,
  userId,
  language: preferredLanguage,
}: DefaultConfigInput): ReportConfig => {
  const reportId = uuidV4();
  const seriesId = uuidV4();
  const generatedDate = new Date().toISOString();
  const resolvedLanguage = i18n.resolvedLanguage || i18n.language || 'en';
  let language: ReportLanguage = preferredLanguage || 'en';

  if (!preferredLanguage) {
    if (resolvedLanguage.startsWith('pl')) {
      language = 'pl';
    } else if (resolvedLanguage.startsWith('sv')) {
      language = 'sv';
    }
  }
  const fallbackProductName =
    productName ||
    translateReportBuilder('defaults.product', {
      lng: language,
    });
  const fallbackOrganizationName =
    organizationName ||
    translateReportBuilder('defaults.organization', {
      lng: language,
    });
  const notProvided = translateReportBuilder('common.notProvided', {
    lng: language,
  });
  const today = new Date().toISOString().slice(0, 10);

  return {
    reportId,
    seriesId,
    projectId: String(organizationId),
    productId: String(productId),
    language,
    languageSource: 'auto',
    reportType: 'environmental-declaration-report',
    templateId: 'modern',
    audience: 'client-ready',
    branding: {
      logoAssetId: organizationLogo?.id ?? null,
      logoUrl: organizationLogo?.url ?? null,
      companyNameOverride: fallbackOrganizationName,
      reportTitle: getDefaultReportTitle(
        'environmental-declaration-report',
        language
      ),
      productName: fallbackProductName,
      showDate: true,
      accentIntensity: 'medium',
      accentColor: DEFAULT_REPORT_ACCENT_COLOR,
      coverStyle: 'envirly',
      coverAssetId: null,
      coverUrl: null,
    },
    layout: {
      density: 'standard',
      chartStyle: 'donut-bars',
      numberFormat: {
        decimals: 2,
        thousandsSeparator: ' ',
        showUnits: true,
      },
      watermark: {
        enabled: false,
        label: translateReportBuilder('defaults.confidentialWatermark', {
          lng: language,
        }),
      },
      footerOptions: {
        showContactInfo: true,
        showWebsite: true,
        showPageNumbers: true,
      },
    },
    sections: REPORT_SECTIONS.map((section) => ({
      id: section.id,
      enabled: section.defaultEnabled,
      detailLevel: 'standard',
      options: getDefaultSectionOptions(section.id),
    })),
    output: {
      formats: ['pdf'],
      reportName: getDefaultReportName(fallbackProductName, today, language),
    },
    audit: {
      datasetVersions: {
        ecoinvent: notProvided,
        defra: notProvided,
        aib: notProvided,
        factorsVersion: notProvided,
      },
      generatedByUserId: userId,
      generatedAt: generatedDate,
      hash: '',
      modelVersion: 'report-builder-1.0.0',
    },
  };
};
